import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate, useBlocker, useBeforeUnload } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Save, ArrowLeft, Globe, EyeOff, Loader2, Check, AlertTriangle } from "lucide-react";
import RichTextEditor from "@/components/RichTextEditor";

type Doc = {
  id: string;
  title: string;
  slug: string;
  content: string;
  status: string;
};

type SaveStatus = "saved" | "unsaved" | "saving" | "error";

export default function DocumentEdit() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [doc, setDoc] = useState<Doc | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>("saved");
  const [slugError, setSlugError] = useState<string | null>(null);

  const savedDocRef = useRef<Doc | null>(null);
  const hasUnsavedChanges = saveStatus === "unsaved" || saveStatus === "error";

  // Block in-app navigation when there are unsaved changes
  const blocker = useBlocker(hasUnsavedChanges);

  // Warn on browser tab close / refresh
  useBeforeUnload(
    useCallback(
      (e: BeforeUnloadEvent) => {
        if (hasUnsavedChanges) {
          e.preventDefault();
        }
      },
      [hasUnsavedChanges]
    )
  );

  useEffect(() => {
    (async () => {
      const { data, error } = await supabase
        .from("documents")
        .select("*")
        .eq("id", id)
        .single();
      if (error) {
        toast.error("Failed to load document");
      }
      setDoc(data);
      savedDocRef.current = data;
      setLoading(false);
    })();
  }, [id]);

  const updateDoc = useCallback((updates: Partial<Doc>) => {
    setDoc((prev) => {
      if (!prev) return prev;
      return { ...prev, ...updates };
    });
    setSaveStatus("unsaved");
    setSlugError(null);
  }, []);

  const checkSlugUniqueness = async (slug: string, docId: string): Promise<boolean> => {
    const { data } = await supabase
      .from("documents")
      .select("id")
      .eq("slug", slug)
      .neq("id", docId)
      .limit(1);
    return !data || data.length === 0;
  };

  const handleSave = async () => {
    if (!doc) return;

    if (!doc.title.trim()) {
      toast.error("Title is required");
      return;
    }
    if (!doc.slug.trim()) {
      toast.error("Slug is required");
      return;
    }

    setSaving(true);
    setSaveStatus("saving");

    const isUnique = await checkSlugUniqueness(doc.slug, doc.id);
    if (!isUnique) {
      setSlugError("This slug is already in use by another document");
      toast.error("Slug already exists. Choose a different one.");
      setSaving(false);
      setSaveStatus("error");
      return;
    }

    const { error } = await supabase
      .from("documents")
      .update({ title: doc.title, slug: doc.slug, content: doc.content, status: doc.status })
      .eq("id", doc.id);

    setSaving(false);

    if (error) {
      toast.error("Failed to save: " + error.message);
      setSaveStatus("error");
    } else {
      toast.success("Document saved");
      setSaveStatus("saved");
      savedDocRef.current = doc;
      setSlugError(null);
    }
  };

  const toggleStatus = async () => {
    if (!doc) return;
    const newStatus = doc.status === "published" ? "draft" : "published";
    setDoc({ ...doc, status: newStatus });
    const { error } = await supabase.from("documents").update({ status: newStatus }).eq("id", doc.id);
    if (error) {
      toast.error("Failed to update status");
      setDoc({ ...doc, status: doc.status });
    } else {
      toast.success(newStatus === "published" ? "Document published" : "Document unpublished");
      if (savedDocRef.current) {
        savedDocRef.current = { ...savedDocRef.current, status: newStatus };
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!doc) {
    return <div className="p-6 text-center text-gray-400">Document not found</div>;
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <Button size="sm" variant="ghost" onClick={() => navigate("/admin/documents")} className="gap-1.5">
          <ArrowLeft className="w-4 h-4" /> Back
        </Button>
        <div className="flex items-center gap-3">
          {/* Autosave status indicator */}
          <div className="flex items-center gap-1.5 text-xs">
            {saveStatus === "saving" && (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400" />
                <span className="text-gray-400">Saving...</span>
              </>
            )}
            {saveStatus === "saved" && (
              <>
                <Check className="w-3.5 h-3.5 text-green-500" />
                <span className="text-green-600">Saved</span>
              </>
            )}
            {saveStatus === "unsaved" && (
              <>
                <div className="w-2 h-2 rounded-full bg-amber-400" />
                <span className="text-amber-600">Unsaved changes</span>
              </>
            )}
            {saveStatus === "error" && (
              <>
                <AlertTriangle className="w-3.5 h-3.5 text-red-500" />
                <span className="text-red-600">Save failed</span>
              </>
            )}
          </div>

          <Button size="sm" variant="outline" onClick={toggleStatus} className="gap-1.5">
            {doc.status === "published" ? (
              <><EyeOff className="w-4 h-4" /> Unpublish</>
            ) : (
              <><Globe className="w-4 h-4" /> Publish</>
            )}
          </Button>
          <Button size="sm" onClick={handleSave} disabled={saving} className="gap-1.5">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save
          </Button>
        </div>
      </div>

      <div className="space-y-4 mb-6">
        <div>
          <label className="text-sm font-medium text-gray-700 mb-1 block">Title</label>
          <input
            type="text"
            value={doc.title}
            onChange={(e) => updateDoc({ title: e.target.value })}
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none"
          />
        </div>
        <div>
          <label className="text-sm font-medium text-gray-700 mb-1 block">Slug</label>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-400">/page/</span>
            <input
              type="text"
              value={doc.slug}
              onChange={(e) => {
                updateDoc({ slug: e.target.value });
                setSlugError(null);
              }}
              className={`flex-1 rounded-lg border px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-primary/30 outline-none ${
                slugError ? "border-red-300 focus:border-red-400 focus:ring-red-200" : "border-gray-200 focus:border-primary"
              }`}
            />
          </div>
          {slugError && (
            <p className="text-xs text-red-500 mt-1">{slugError}</p>
          )}
        </div>
      </div>

      <RichTextEditor
        content={doc.content}
        onChange={(html) => updateDoc({ content: html })}
      />

      {/* Navigation blocker dialog */}
      <AlertDialog open={blocker.state === "blocked"}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Are you sure you want to leave? Your changes will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => blocker.reset?.()}>
              Stay on Page
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => blocker.proceed?.()}
              className="bg-red-600 hover:bg-red-700"
            >
              Leave Without Saving
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
